import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, type ErrorComponentProps } from '@tanstack/react-router'
import { useId } from 'react'

import ScreenLayout from '@/components/layout/ScreenLayout'
import EmptyState from '@/components/ui/EmptyState'
import NotAvailable from '@/components/ui/NotAvailable'
import TabBar, { TabPanel, type TabBarOption } from '@/components/ui/TabBar'
import TitleRow from '@/components/ui/TitleRow'
import { stateActionClass } from '@/components/ui/empty-state-variants'
import { toBsDate } from '@/lib/bsDate'
import { companyDetailQueryOptions } from '@/features/company/api/queries'
import { useSignalTechnicals } from '@/features/signals/api/queries'
import PivotLevelsBar from '@/screens/signals/components/PivotLevelsBar'
import PivotTable from '@/screens/signals/components/PivotTable'
import SignalCandleChart from '@/screens/signals/components/SignalCandleChart'
import SignalGauge from '@/screens/signals/components/SignalGauge'
import SignalStockHeader from '@/screens/signals/components/SignalStockHeader'
import SignalStockViewLink from '@/screens/signals/components/SignalStockViewLink'
import {
  SignalStockHeaderSkeleton,
  SignalTechnicalsBodySkeleton,
} from '@/screens/signals/components/SignalStockSkeletons'
import SignalTechnicalsTable from '@/screens/signals/components/SignalTechnicalsTables'
import SignalsEmpty from '@/screens/signals/components/SignalsEmpty'
import SignalsFootnote from '@/screens/signals/components/SignalsFootnote'
import SignalsRouteError from '@/screens/signals/components/SignalsRouteError'
import { signalsRailProps } from '@/screens/signals/components/signals-rail'
import { signalSymbolOf, type SignalStockSource } from '@/screens/signals/SignalStockScreen.loader'
import {
  SIGNAL_TECHNICALS_TABS,
  loadSignalTechnicals,
  signalTechnicalsSearchSchema,
} from '@/screens/signals/SignalTechnicalsScreen.loader'

/** Where Back goes: the board the member opened the Signal page from. */
const BACK = {
  live: '/market/signals/live',
  daily: '/market/signals/daily',
} as const satisfies Record<SignalStockSource, string>

/** The board's own name, for the not-found page's way back. */
const BOARD = { live: 'Live signals', daily: 'Daily forecast' } as const

const TAB_OPTIONS: readonly TabBarOption[] = [
  { value: 'oscillators', label: 'Oscillators' },
  { value: 'averages', label: 'Moving Averages' },
  { value: 'pivots', label: 'Pivots' },
]

/** "Monthly levels, Asoj 2083": the BS month the pivots were built from. */
const monthlyLevels = (month: string): string =>
  `Monthly levels, ${toBsDate(month)?.format('MMMM YYYY', 'en') ?? '—'}`

/** The URL's ticker as the page shows it, valid or not: the pending and error frames print it. */
const shownSymbol = (raw: string): string => signalSymbolOf(raw) ?? raw.trim().toUpperCase()

/*
 * The loaded page, block for block: the header with the URL's ticker, the tab
 * row, the view the URL opens on, the note and the candle chart
 * (`SignalStockSkeletons.tsx`).
 */
function SignalTechnicalsPending() {
  const { symbol } = Route.useParams()
  const { tab, from } = Route.useSearch()
  const shown = shownSymbol(symbol)
  return (
    <ScreenLayout
      pending
      pendingLabel={`Loading technicals for ${shown}…`}
      header={
        <SignalStockHeaderSkeleton
          symbol={shown}
          back={BACK[from]}
          action={<SignalStockViewLink to="signal" symbol={shown} from={from} />}
        />
      }
      {...signalsRailProps(shown)}
    >
      <SignalTechnicalsBodySkeleton tab={tab} />
    </ScreenLayout>
  )
}

function SignalTechnicalsError(props: ErrorComponentProps) {
  const { symbol } = Route.useParams()
  const { from } = Route.useSearch()
  const shown = shownSymbol(symbol)
  return (
    <SignalsRouteError
      {...props}
      header={<TitleRow title={shown} back={BACK[from]} />}
      pageTitle={shown}
      title={`Technicals for ${shown} did not load`}
    />
  )
}

export const Route = createFileRoute('/_app/market/signals/stock/$symbol/technicals')({
  validateSearch: signalTechnicalsSearchSchema,
  loader: loadSignalTechnicals,
  pendingComponent: SignalTechnicalsPending,
  errorComponent: SignalTechnicalsError,
  component: SignalTechnicalsScreen,
})

/** Not a symbol mm-be lists: the way back is the board the page was opened from. */
function SignalTechnicalsNotFound({ symbol, from }: { symbol: string; from: SignalStockSource }) {
  return (
    <ScreenLayout
      header={<TitleRow title={symbol} back={BACK[from]} />}
      {...signalsRailProps(symbol)}
    >
      <EmptyState
        title="Stock not found"
        description={`No listed company has the symbol ${symbol}.`}
        action={
          <Link to={BACK[from]} className={stateActionClass('page')}>
            Back to {BOARD[from]}
          </Link>
        }
      />
    </ScreenLayout>
  )
}

/**
 * Signal Zone · Technicals (f25–f27) — one stock's oscillators, moving
 * averages and pivots under underline tabs (`?tab=`), with the Signal page's
 * header, candle chart and footnote.
 *
 * MAIN read keyed on the path's symbol alone, so suspense (the loader always
 * has it): the stock's technicals, every row, word, gauge and pivot level
 * mm-be's own — the page computes none of them. Its `null` is not-found. The
 * company read fills the header's name and sector, as on the Signal page.
 * `oscillatorRating`, `movingAverageRating` and `pivots` ride in the same
 * answer for the gauges and the pivot levels.
 */
function SignalTechnicalsScreen() {
  const { symbol: raw } = Route.useParams()
  const { from } = Route.useSearch()
  const symbol = signalSymbolOf(raw)
  // Before any suspense read: the loader asked for nothing for an invalid symbol.
  if (symbol === null) return <SignalTechnicalsNotFound symbol={shownSymbol(raw)} from={from} />
  return <SignalTechnicalsPage symbol={symbol} />
}

function SignalTechnicalsPage({ symbol }: { symbol: string }) {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const idBase = useId()
  const technicals = useSignalTechnicals(symbol).data
  const company = useQuery({ ...companyDetailQueryOptions(symbol), enabled: technicals !== null })

  if (technicals === null) {
    return <SignalTechnicalsNotFound symbol={symbol} from={search.from} />
  }

  const header = (
    <SignalStockHeader
      symbol={symbol}
      back={BACK[search.from]}
      name={company.data?.name ?? null}
      sector={company.data?.sector ?? null}
      logo={company.data?.profile.logoUrl ?? null}
      identityPending={company.data === undefined && !company.isError}
      action={<SignalStockViewLink to="signal" symbol={symbol} from={search.from} />}
    />
  )

  const rating =
    search.tab === 'averages' ? technicals.movingAverageRating : technicals.oscillatorRating

  const table = (kind: 'oscillators' | 'averages') => {
    const rows = kind === 'averages' ? technicals.movingAverages : technicals.oscillators
    // A listed stock with too little price history: mm-be read nothing at all.
    if (rows.length === 0) {
      return (
        <SignalsEmpty
          reason="list"
          size="section"
          title={`No technical readings for ${symbol} yet`}
          description="They need more sessions of prices than the stock has."
        />
      )
    }
    return <SignalTechnicalsTable kind={kind} rows={rows} />
  }

  const pivots = technicals.pivots
  const view =
    search.tab === 'pivots' ? (
      pivots === null ? (
        <NotAvailable thing="Pivot levels" />
      ) : (
        <div className="flex flex-col gap-6">
          <SignalGauge word={pivots.rating.word} score={pivots.rating.score} />
          <p className="text-h-s text-muted-foreground">{monthlyLevels(pivots.month)}</p>
          <PivotLevelsBar levels={pivots.classic} />
          <PivotTable
            classic={pivots.classic}
            fibonacci={pivots.fibonacci}
            camarilla={pivots.camarilla}
            woodie={pivots.woodie}
          />
        </div>
      )
    ) : (
      <div className="flex flex-col gap-6">
        <SignalGauge word={rating.word} score={rating.score} />
        {table(search.tab)}
      </div>
    )

  return (
    <ScreenLayout header={header} {...signalsRailProps(symbol)}>
      <TabBar
        ariaLabel={`${symbol} technical views`}
        options={TAB_OPTIONS}
        value={search.tab}
        idBase={idBase}
        onChange={(value) => {
          const tab = SIGNAL_TECHNICALS_TABS.find((option) => option === value) ?? 'oscillators'
          void navigate({ search: (prev) => ({ ...prev, tab }) })
        }}
      />
      <TabPanel idBase={idBase} value={search.tab}>
        {view}
      </TabPanel>
      <SignalsFootnote />
      <section aria-label={`${symbol} price chart`}>
        <SignalCandleChart symbol={symbol} name={symbol} date={null} />
      </section>
    </ScreenLayout>
  )
}
